import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, InputLabel } from '@mui/material';
import { useFormik } from 'formik';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import equipmentApi from '~/apis/equipment';
import { CloseIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import useLanguage from '~/hooks/useLanguage';
import { useAppSelector } from '~/redux/hooks';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { EQUIPMENT_NAMES_4_ZONE } from '~/utils/constants/equipment';
import { cast, handleApiError, notify } from '~/utils/helper';
import { IAddSensor, IEquipment, ISensorPort } from '~/utils/interface/equipment';
import equipmentValidation from '~/validation/equipment';

type EditSensorDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  equipment?: IEquipment;
  mode: string;
  data?: ISensorPort;
};

const initialValues: IAddSensor = {
  sensor_code: '',
  sensor_type: '',
};

const EditSensorDialog = (props: EditSensorDialogProps) => {
  const { t } = useTranslation('admin/equipment');
  const language = useLanguage();
  const { sensorTypes } = useAppSelector((state) => state.master);

  const sensorTypeOptions = useMemo(() => {
    return sensorTypes.map((sensorType: any) => {
      return {
        id: sensorType.id,
        name: language.getValue('string_value', sensorType),
      };
    });
  }, [language, sensorTypes]);

  const sensorCodeOptions = useMemo(() => {
    let totalAddress = 20;
    if (props?.equipment?.device_name === EQUIPMENT_NAMES_4_ZONE) {
      totalAddress = 80;
    }
    return Array.from({ length: totalAddress }, (_, index) => {
      const address = `${props?.equipment?.device_code}_01${String(index + 1).padStart(2, '0')}`;
      return { id: address, name: address };
    });
  }, [props?.equipment]);

  const form = useFormik({
    validateOnChange: false,
    validationSchema: equipmentValidation(t).addSensor,
    initialValues,
    onSubmit: async (values) => {
      const device_code = String(props.equipment?.device_code);
      if (props.mode === DIALOG_MODE_CREATE) {
        try {
          await equipmentApi.addSensor({ ...values, device_code });
          notify('success', t('editSensorDialog.addSuccess'));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e, true);
        }
      }
      if (props.mode === DIALOG_MODE_EDIT) {
        try {
          await equipmentApi.updateSensor({ ...values, device_code });
          notify('success', t('editSensorDialog.updateSuccess'));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e, true);
        }
      }
    },
  });

  useEffect(() => {
    if (props.open && props.mode === DIALOG_MODE_EDIT && props.data) {
      form.setValues({
        sensor_code: cast(props.data?.sensor_code),
        sensor_type: cast(props.data?.sensor_type),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.data]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='sm'>
      <DialogTitle className='bg-[#005F73]'>
        {props.mode === DIALOG_MODE_EDIT ? t('editSensorDialog.editTitle') : t('editSensorDialog.title')}
        <IconButton onClick={() => handleSetOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px] !items-center'>
            <InputLabel className='w-1/2 font-bold flex items-center'>{t('editSensorDialog.deviceCode')}:</InputLabel>
            <InputLabel className='w-1/2 font-bold flex items-center justify-end'>
              {props?.equipment?.device_code}
            </InputLabel>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-1/2 font-bold flex items-center'>
              <InputLabel required={true}>{t('editSensorDialog.sensorCode')}</InputLabel>
              {' : '}
            </Box>
            <Box className='w-1/2'>
              {props?.mode === DIALOG_MODE_CREATE ? (
                <BaseSelect
                  size='medium'
                  options={sensorCodeOptions}
                  value={form.values.sensor_code}
                  placeholder={t('select')}
                  name='sensor_code'
                  isSearchEnable
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.sensor_code)}
                  helperText={form.errors?.sensor_code}
                  displayInvalidValue
                />
              ) : (
                <InputLabel className='font-bold flex items-center justify-end'>{props?.data?.sensor_code}</InputLabel>
              )}
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-1/2 font-bold flex items-center'>
              <InputLabel required={true}>{t('editSensorDialog.sensorType')}</InputLabel>
              {' : '}
            </Box>
            <Box className='w-1/2'>
              <BaseSelect
                size='medium'
                options={sensorTypeOptions}
                value={form.values.sensor_type}
                placeholder={t('select')}
                name='sensor_type'
                isSearchEnable
                onChange={form.handleChange}
                error={Boolean(form.errors?.sensor_type)}
                helperText={form.errors?.sensor_type}
                displayInvalidValue
              />
            </Box>
          </BaseRow>
          <Button
            variant='contained'
            color='secondary'
            className='min-w-[125px] flex ml-auto'
            onClick={() => form.handleSubmit()}
          >
            {t('editSensorDialog.add')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(EditSensorDialog);
